import { HTMLAttributes, ReactNode } from "react";
import { cx } from "../../utils/cx";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import styles from "./MessageBanner.module.scss";

/** Up to two actions; order is preserved (e.g. primary then secondary). */
export type MessageBannerAction = {
    label: string;
    onClick: () => void;
    variant?: "primary" | "secondary";
};

export type MessageBannerProps = Omit<HTMLAttributes<HTMLElement>, "title"> & {
    /** Optional bold line above the description. */
    title?: ReactNode;
    /** Main copy (required). */
    description: ReactNode;
    /** Optional leading icon (e.g. 24×24); reserves a fixed column when set. */
    icon?: ReactNode;
    /** Zero, one, or two buttons. One button aligns end; two align start (Figma Action area). */
    actions?: MessageBannerAction[];
    /** When set, shows a dismiss control (e.g. close) at the trailing edge. */
    onDismiss?: () => void;
    /** Accessible name for the banner region (defaults to a generic label). */
    "aria-label"?: string;
};

const defaultAriaLabel = "Message";

const iconClose = (
    <svg aria-hidden fill="none" viewBox="0 0 24 24">
        <path
            d="m7 7 10 10M17 7 7 17"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2"
        />
    </svg>
);

/**
 * Treez DS — Message Banner.
 *
 * Design: Figma `Message Banner` — file `qgFv3OQga6RQ4luvSFuGFP`
 * (Desktop / Mobile + Action area). Tokens: `@/theme/tokens.scss`.
 */
export function MessageBanner({
    title,
    description,
    icon,
    actions,
    onDismiss,
    className,
    "aria-label": ariaLabel = defaultAriaLabel,
    ...rest
}: MessageBannerProps) {
    const safeActions = (actions ?? []).slice(0, 2);
    const hasActions = safeActions.length > 0;

    return (
        <section
            aria-label={ariaLabel}
            className={cx(styles.root, className)}
            role="region"
            {...rest}
        >
            <div className={styles.inner}>
                <div className={styles.main}>
                    {icon ? (
                        <div className={styles.iconSlot}>{icon}</div>
                    ) : null}
                    <div className={styles.body}>
                        <div className={styles.text}>
                            {title ? (
                                <p className={styles.title}>{title}</p>
                            ) : null}
                            <div className={styles.description}>
                                {description}
                            </div>
                        </div>
                        {hasActions ? (
                            <div
                                className={cx(
                                    styles.actions,
                                    safeActions.length === 1 &&
                                        styles.actionsSingle,
                                )}
                            >
                                {safeActions.map((a, i) => (
                                    <Button
                                        key={`${a.label}-${i}`}
                                        onClick={a.onClick}
                                        size="sm"
                                        type="button"
                                        variant={a.variant ?? "primary"}
                                    >
                                        {a.label}
                                    </Button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
                {onDismiss ? (
                    <IconButton
                        aria-label="Dismiss"
                        className={styles.dismiss}
                        onClick={onDismiss}
                        rounded
                        size="sm"
                        type="button"
                        variant="tertiary"
                    >
                        {iconClose}
                    </IconButton>
                ) : null}
            </div>
        </section>
    );
}

MessageBanner.displayName = "MessageBanner";
